import { Badge, Button, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner, TabContent, TabPane } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { useTranslation } from "react-i18next";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { useRef, useState, useEffect } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import {
  IoAttachOutline, IoCalendarOutline, IoCallOutline, IoCopyOutline, IoCreateOutline,
  IoGlobeOutline, IoLocationOutline, IoLogoWhatsapp, IoMailOutline, IoMegaphoneOutline,
  IoPersonOutline, IoReaderOutline, IoStatsChartOutline, IoTrashOutline,
  IoTrendingUpOutline, IoWalletOutline
} from "react-icons/io5";
import OverviewTab from "./Tabs/Overview/Overview";
import Timeline from "../../components/Timeline";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get, post } from "../../../../utils/helpers/api";
import DefaultButton from "../../../../components/Buttons/Default";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import { useSettings } from "../../../../context/settings";
import { formatMoney, useCurrency } from "../../../../utils/helpers/currency";
import { LEAD_STAGES, leadStatusLabel, personName, printLeadStatus } from "../../utils/helpers";
import {
  Avatar, Fact, initialsOf, RecordLayout, RecordMain, RecordNav, RecordRail, RecordTabs,
  Stages, StatStrip, useScrollSpy
} from "../../components/Record";

function ViewLeadPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const baseCurrency = useCurrency();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [leadLoading, setLeadLoading] = useState(true);
  const [leadData, setLeadData] = useState(state?.leadData || {});
  const [convertOpen, setConvertOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const NAV_SECTIONS = [
    { id: 'lead-informations-card', label: t('crm.leadInformation'), icon: <IoPersonOutline size={14} /> },
    { id: 'address-card', label: t('common.address'), icon: <IoLocationOutline size={14} /> },
    { id: 'notes-card', label: t('common.notes'), icon: <IoReaderOutline size={14} /> },
    { id: 'attachments-card', label: t('attachments.title'), icon: <IoAttachOutline size={14} /> }
  ];

  const { activeSection, scrollToSection } = useScrollSpy(
    NAV_SECTIONS.map(section => section.id),
    scrollContainerRef,
    activeTab === 'overview',
    leadData?.id
  );

  useEffect(() => {
    if (id) {
      getLead();
    }
  }, [id]);

  const getLead = async () => {
    setLeadLoading(true);
    const data = await get('/crm/leads/view/' + id);
    if (data?.lead) {
      setLeadData(data.lead);
      setLeadLoading(false);
    }
  };

  const convertLead = async () => {
    const data = await post('/crm/leads/convert/' + id);
    if (data?.success) {
      navigate('/crm/customers/view/' + data.customer?.id, { state: { customerData: data.customer } });
    }
  };

  const cloneLead = async () => {
    const data = await post('/crm/leads/clone/' + id);
    if (data?.lead) {
      navigate('/crm/leads/edit/' + data.lead.id, { state: { leadData: data.lead } });
    }
  };

  const deleteLead = async () => {
    const data = await del('/crm/leads/delete/' + id);
    if (data?.success) {
      toast.success(data.message || t('crm.leadDeleted'));
      navigate('/crm/leads');
    }
  };

  /* The update endpoint always expects the lead name, everything else it leaves untouched. */
  const changeStatus = async (status) => {
    const data = await post('/crm/leads/update/' + id, { name: leadData.name, status });
    if (data?.success) {
      setLeadData(prev => ({ ...prev, status }));
    }
  };

  const leadName = leadData?.name
    || [leadData?.firstname, leadData?.lastname].filter(Boolean).join(' ')
    || t('crm.lead');

  const currency = (leadData?.currency || baseCurrency || '').toUpperCase();
  const money = (value) => `${formatMoney(value)} ${currency}`;

  const expectedValue = Number(leadData?.expected_value ?? 0);
  const probability = Number(leadData?.probability ?? 0);
  const location = [leadData?.city, leadData?.state, leadData?.country].filter(Boolean).join(', ');
  const phone = [leadData?.country_code, leadData?.phone_number].filter(Boolean).join(' ');
  const whatsapp = [leadData?.whatsapp_country_code, leadData?.whatsapp].filter(Boolean).join(' ');
  const stages = LEAD_STAGES.map(stage => ({ value: stage, label: t('crm.' + stage) }));

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={leadName}
          onBack={() => navigate('/crm/leads')}
        >
          <Dropdown isOpen={statusOpen} toggle={() => setStatusOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary" caret>
              {leadStatusLabel(leadData?.status) || t('crm.changeStatus')}
            </DropdownToggle>
            <DropdownMenu end>
              {[...LEAD_STAGES, 'lost'].map(status => (
                <DropdownItem
                  key={status}
                  active={leadData?.status === status}
                  onClick={() => changeStatus(status)}
                >
                  {t('crm.' + status)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <DefaultButton size="sm" color="primary" onClick={() => setConvertOpen(true)}>
            {t('crm.convertLead')}
          </DefaultButton>
          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => navigate(`/crm/leads/edit/${id}`, { state: { leadData } })}>
                <IoCreateOutline size={15} /> {t('common.edit')}
              </DropdownItem>
              <DropdownItem onClick={cloneLead}>
                <IoCopyOutline size={15} /> {t('common.clone')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable" ref={scrollContainerRef}>
          {leadLoading ?
            <Spinner color="primary" className="d-block mx-auto my-4" />
          :
            <RecordLayout>
              <RecordRail>
                <Card>
                  <CardBody className="text-center pb-3">
                    <Avatar initials={initialsOf(leadName)} fallback={<IoPersonOutline size={22} />} />

                    <h6 className="fw-bold mb-1 mt-3">{leadName}</h6>
                    {leadData?.company_name && (
                      <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                        {leadData.company_name}
                      </div>
                    )}
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      {printLeadStatus(leadData?.status)}
                      {leadData?.type && <Badge color="light">{t('crm.' + leadData.type)}</Badge>}
                    </div>

                    <div className="d-flex justify-content-center gap-2 mt-3">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!leadData?.email}
                        onClick={() => { window.location.href = `mailto:${leadData.email}`; }}
                      >
                        <IoMailOutline size={14} />
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!leadData?.phone_number}
                        onClick={() => { window.location.href = `tel:${leadData.phone_number}`; }}
                      >
                        <IoCallOutline size={14} />
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!leadData?.whatsapp}
                        onClick={() => window.open(`https://wa.me/${whatsapp.replace(/\D/g, '')}`, '_blank')}
                      >
                        <IoLogoWhatsapp size={14} />
                      </Button>
                    </div>
                  </CardBody>

                  <hr className="my-0" />

                  <CardBody className="d-flex flex-column gap-2">
                    <Fact icon={<IoMailOutline size={14} />} value={leadData?.email} href={`mailto:${leadData?.email}`} />
                    <Fact icon={<IoCallOutline size={14} />} value={phone} href={`tel:${leadData?.phone_number}`} />
                    <Fact icon={<IoLogoWhatsapp size={14} />} value={whatsapp} />
                    <Fact
                      icon={<IoGlobeOutline size={14} />}
                      value={leadData?.website}
                      href={leadData?.website?.startsWith('http') ? leadData.website : `https://${leadData?.website}`}
                    />
                    <Fact icon={<IoLocationOutline size={14} />} value={location} />
                    <Fact icon={<IoPersonOutline size={14} />} value={personName(leadData?.owner)} />
                    <Fact icon={<IoMegaphoneOutline size={14} />} value={leadData?.source} />
                    <Fact
                      icon={<IoCalendarOutline size={14} />}
                      value={leadData?.created_at ? formatDate(leadData.created_at) : null}
                    />
                  </CardBody>
                </Card>

                {activeTab === 'overview' && (
                  <RecordNav sections={NAV_SECTIONS} activeId={activeSection} onSelect={scrollToSection} />
                )}
              </RecordRail>

              <RecordMain>
                <StatStrip
                  tiles={[
                    {
                      icon: <IoWalletOutline size={18} />,
                      background: '#f0eeff',
                      color: '#6C63FF',
                      value: money(expectedValue),
                      label: t('crm.expectedValue')
                    },
                    {
                      icon: <IoStatsChartOutline size={18} />,
                      background: '#e6f4ff',
                      color: '#1d7afc',
                      value: `${probability}%`,
                      label: t('crm.probability')
                    },
                    {
                      icon: <IoTrendingUpOutline size={18} />,
                      background: '#e9f9f0',
                      color: '#22a06b',
                      value: money(expectedValue * probability / 100),
                      label: t('crm.weightedValue')
                    },
                    {
                      icon: <IoCalendarOutline size={18} />,
                      background: '#fff3e6',
                      color: '#fd7e14',
                      value: leadData?.expected_close_date ? formatDate(leadData.expected_close_date) : '-',
                      label: t('crm.expectedCloseDate')
                    }
                  ]}
                >
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="crm-record-label">{t('crm.stage')}</span>
                      <span className="fw-bold" style={{ fontSize: '0.8rem' }}>
                        {leadStatusLabel(leadData?.status) || '-'}
                      </span>
                    </div>
                    <Stages
                      stages={stages}
                      current={leadData?.status}
                      color={leadData?.status === 'won' ? '#22a06b' : 'var(--nazar-primary)'}
                    />
                  </div>
                </StatStrip>

                <RecordTabs
                  tabs={[
                    { id: 'overview', label: t('crm.overview') },
                    { id: 'timeline', label: t('crm.timeline') }
                  ]}
                  activeTab={activeTab}
                  onChange={setActiveTab}
                />
                <TabContent activeTab={activeTab} className="border-0 p-0">
                  <TabPane tabId="overview">
                    <OverviewTab leadData={leadData} />
                  </TabPane>
                  <TabPane tabId="timeline">
                    <Timeline subjectType="lead" subjectId={id} />
                  </TabPane>
                </TabContent>
              </RecordMain>
            </RecordLayout>
          }
        </div>
      </div>

      <ConfirmationModal
        isOpen={convertOpen}
        toggle={() => setConvertOpen(false)}
        onConfirm={convertLead}
        title={t('crm.convertLead')}
        message={t('crm.convertLeadConfirm')}
        confirmationText={t('crm.convertLead')}
        confirmColor="primary"
      />

      <ConfirmationModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        onConfirm={deleteLead}
        title={t('crm.deleteLead')}
        message={t('crm.deleteLeadConfirm')}
      />
    </SidebarLayout>
  );
}

export default ViewLeadPage;
